import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Header,
  Input,
  SpaceBetween,
  Toggle,
} from "@amzn/awsui-components-react";

import { RootState } from "../../app/rootReducer";
import { formatMatchCount } from "../validationDashboardTable/formatMatchCount";
import {
  applyOpenOnlyFilter,
  applyTextFilter,
  RollupCounts,
  selectProgramTruncation,
  selectRollupCounts,
  selectVisibleRows,
} from "./programTicketsConnector";
import { rollupStatusOf } from "./rollupStatus";
import { ProgramTicket } from "./types";

const DEBOUNCE_MS = 300;

function useDebouncedCallback<Args extends unknown[]>(
  fn: (...args: Args) => void,
  ms: number
): (...args: Args) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== undefined) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (...args: Args) => {
      if (timeoutRef.current !== undefined) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        fnRef.current(...args);
      }, ms);
    },
    [ms]
  );
}

function countRollups(rows: readonly ProgramTicket[]): RollupCounts {
  let open = 0;
  let wip = 0;
  let resolved = 0;
  for (const row of rows) {
    switch (rollupStatusOf(row.status)) {
      case "open":
        open += 1;
        break;
      case "work-in-progress":
        wip += 1;
        break;
      case "resolved":
        resolved += 1;
        break;
    }
  }
  return { open, wip, resolved };
}

export interface ProgramTicketsHeaderContentProps {
  readonly visibleRows?: readonly ProgramTicket[];
}

/**
 * Renders the Cloudscape `<Header variant="h2">` for the Program
 * Tickets `<Table>`'s `header` slot. Includes the counter (server
 * `totalCount`) in the title's counter chip and the rollup summary in
 * the description slot.
 */
export const ProgramTicketsTableHeader: React.FC<
  ProgramTicketsHeaderContentProps
> = ({ visibleRows: visibleRowsProp }) => {
  const totalCount = useSelector(
    (state: RootState) => state.programTickets.totalCount
  );
  const selectorVisibleRows = useSelector(selectVisibleRows);
  const selectorRollupCounts = useSelector(selectRollupCounts);
  const truncation = useSelector(selectProgramTruncation);

  const visibleRows = visibleRowsProp ?? selectorVisibleRows;
  const counts = useMemo<RollupCounts>(
    () =>
      visibleRowsProp === undefined
        ? selectorRollupCounts
        : countRollups(visibleRowsProp),
    [visibleRowsProp, selectorRollupCounts]
  );

  const descriptionParts: string[] = [];
  if (visibleRows.length > 0) {
    descriptionParts.push(
      `This page: ${counts.open} Open, ${counts.wip} Work in Progress, ${counts.resolved} Resolved`
    );
  }
  if (truncation !== undefined) {
    descriptionParts.push(
      `Showing tickets from the top ${truncation.shown} of ${truncation.total} programs.`
    );
  }
  const description =
    descriptionParts.length > 0 ? descriptionParts.join(" ") : undefined;

  return (
    <Header
      variant="h2"
      counter={`(${formatMatchCount(totalCount)})`}
      description={description}
    >
      Program Tickets
    </Header>
  );
};

/**
 * Renders the filter input and Open-only toggle for the Program
 * Tickets `<Table>`'s `filter` slot. Debounces text input at 300ms
 * before dispatching the server-side `/tickets` refetch.
 */
export const ProgramTicketsTableFilter: React.FC = () => {
  const dispatch = useDispatch<any>();

  const openOnly = useSelector(
    (state: RootState) => state.programTickets.openOnly
  );
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );

  const [localFilter, setLocalFilter] = useState<string>("");
  const commitFilter = useCallback(
    (next: string) => {
      dispatch(applyTextFilter(next, queryString));
    },
    [dispatch, queryString]
  );
  const debouncedCommitFilter = useDebouncedCallback(commitFilter, DEBOUNCE_MS);

  return (
    <SpaceBetween direction="horizontal" size="s">
      <Input
        value={localFilter}
        placeholder="Filter tickets..."
        onChange={({ detail }) => {
          setLocalFilter(detail.value);
          debouncedCommitFilter(detail.value);
        }}
      />
      <Toggle
        checked={openOnly}
        onChange={({ detail }) => {
          dispatch(applyOpenOnlyFilter(detail.checked, queryString));
        }}
      >
        Open only
      </Toggle>
    </SpaceBetween>
  );
};
